/**
 * California Regions Mapping Service
 * Maps counties to 10 California regions
 */

export interface RegionInfo {
  id: number;
  name: string;
  counties: string[];
  pumaCodes: number[]; // Add PUMA codes for each region
}

export const CALIFORNIA_REGIONS: RegionInfo[] = [
  {
    id: 1,
    name: 'Superior California',
    counties: ['Butte', 'Colusa', 'El Dorado', 'Glenn', 'Lassen', 'Modoc', 'Nevada', 'Placer', 'Plumas', 'Sacramento', 'Shasta', 'Sierra', 'Siskiyou', 'Sutter', 'Tehama', 'Yolo', 'Yuba'],
    pumaCodes: [700, 1100, 1500, 1700, 5700, 6101, 6102, 6103, 6701, 6703, 6704, 6705, 6706, 6707, 6709, 6712, 6713, 6714, 6715, 6716, 6717, 8900, 10100, 11301, 11302]
  },
  {
    id: 2,
    name: 'North Coast',
    counties: ['Del Norte', 'Humboldt', 'Lake', 'Mendocino', 'Napa', 'Sonoma', 'Trinity'],
    pumaCodes: [1100, 1500, 1700, 2300, 3300, 3734, 3764, 3774, 5500, 5921, 6516, 7104, 7307, 9702, 9704, 9705, 9706]
  },
  {
    id: 3,
    name: 'San Francisco Bay Area',
    counties: ['Alameda', 'Contra Costa', 'Marin', 'San Francisco', 'San Mateo', 'Santa Clara', 'Solano'],
    pumaCodes: [101, 111, 112, 113, 114, 115, 116, 117, 118, 119, 120, 121, 122, 123, 1301, 1305, 1308, 1309, 1310, 1311, 1312, 1313, 1314, 4103, 4104, 5500, 7507, 7508, 7509, 7510, 7511, 7512, 7513, 7514, 8101, 8102, 8103, 8104, 8105, 8106, 8505, 8506, 8507, 8508, 8510, 8511, 8512, 8515, 8516, 8517, 8518, 8519, 8520, 8521, 8522, 9501, 9502, 9503, 9702, 9704, 9705, 9706]
  },
  {
    id: 4,
    name: 'Northern San Joaquin Valley',
    counties: ['Alpine', 'Amador', 'Calaveras', 'Madera', 'Mariposa', 'Merced', 'Mono', 'San Joaquin', 'Stanislaus', 'Tuolumne'],
    pumaCodes: [300, 3900, 4701, 4702, 7302, 7705, 7706, 7707, 7708, 7709, 9901, 9902, 9903, 9904]
  },
  {
    id: 5,
    name: 'Central Coast',
    counties: ['Monterey', 'San Benito', 'San Luis Obispo', 'Santa Barbara', 'Santa Cruz', 'Ventura'],
    pumaCodes: [3737, 5301, 5302, 5303, 7901, 7902, 8301, 8302, 8303, 8701, 8702, 11102, 11103, 11104, 11107, 11108, 11109]
  },
  {
    id: 6,
    name: 'San Joaquin Valley',
    counties: ['Fresno', 'Inyo', 'Kern', 'Kings', 'Tulare'],
    pumaCodes: [300, 1901, 1902, 1903, 1904, 1905, 1906, 1907, 2903, 2904, 2905, 2906, 2907, 2908, 3100, 10701, 10702, 10703]
  },
  {
    id: 7,
    name: 'Inland Empire',
    counties: ['Riverside', 'San Bernardino'],
    pumaCodes: [6501, 6502, 6507, 6509, 6511, 6514, 6515, 6516, 6517, 6518, 6519, 6520, 6521, 6522, 6523, 6524, 6525, 7101, 7102, 7103, 7104, 7105, 7107, 7108, 7110, 7111, 7112, 7115, 7116, 7117, 7118, 7119]
  },
  {
    id: 8,
    name: 'Los Angeles County',
    counties: ['Los Angeles'],
    pumaCodes: [3703, 3704, 3705, 3706, 3707, 3708, 3709, 3710, 3711, 3712, 3713, 3714, 3715, 3716, 3717, 3718, 3719, 3720, 3721, 3722, 3723, 3724, 3725, 3728, 3730, 3731, 3732, 3733, 3734, 3735, 3736, 3737, 3738, 3739, 3740, 3741, 3742, 3743, 3744, 3745, 3746, 3747, 3748, 3750, 3751, 3752, 3753, 3754, 3757, 3758, 3759, 3760, 3761, 3762, 3763, 3764, 3766, 3767, 3768, 3770, 3771, 3772, 3773, 3774, 3775, 3776, 3778, 3779, 3780, 3781, 3782]
  },
  {
    id: 9,
    name: 'Orange County',
    counties: ['Orange'],
    pumaCodes: [5906, 5907, 5908, 5909, 5912, 5913, 5914, 5916, 5917, 5918, 5919, 5920, 5921, 5922, 5923, 5924, 5925, 5926, 5927, 5928, 6712]
  },
  {
    id: 10,
    name: 'San Diego-Imperial',
    counties: ['Imperial', 'San Diego'],
    pumaCodes: [2500, 7301, 7302, 7306, 7307, 7308, 7310, 7311, 7312, 7313, 7314, 7315, 7316, 7317, 7322, 7323, 7324, 7325, 7326, 7327, 7328, 7329, 7330]
  }
];

export const getRegionById = (id: number): RegionInfo | undefined => {
  return CALIFORNIA_REGIONS.find(region => region.id === id);
};

export const getRegionByCounty = (countyName: string): RegionInfo | undefined => {
  const cleanedCountyName = countyName.replace(/ County$/, ''); // Remove " County" suffix
  return CALIFORNIA_REGIONS.find(region => 
    region.counties.includes(cleanedCountyName)
  );
};

export const getRegionName = (regionId: number): string => {
  if (regionId === 0) {
    return 'All California';
  }
  const region = getRegionById(regionId);
  return region ? region.name : 'Unknown Region';
};

export const getPumaCodesForRegion = (regionId: number): number[] => {
  // Special case: regionId 0 means "All California" - return empty array to show all PUMAs
  if (regionId === 0) {
    return [];
  }
  const region = getRegionById(regionId);
  return region ? region.pumaCodes : [];
};

/**
 * Get map center coordinates for a region
 * @param regionId Region ID (0 = All California, 1-10 = specific regions)
 * @returns [latitude, longitude] tuple for map center
 */
export const getRegionCenter = (regionId: number): [number, number] => {
  // Region center coordinates based on geographic centers of each region
  const regionCenters: Record<number, [number, number]> = {
    0: [36.7783, -119.4179], // All California - center near Fresno
    1: [39.5, -121.0], // Superior California - centered on Sacramento area
    2: [40.0, -123.5], // North Coast - centered on Humboldt/Mendocino
    3: [37.7749, -122.4194], // San Francisco Bay Area - San Francisco
    4: [37.6, -120.7], // Northern San Joaquin Valley - centered on Stockton/Modesto area
    5: [36.0, -120.5], // Central Coast - centered on Monterey/San Luis Obispo
    6: [36.0, -119.5], // San Joaquin Valley - centered on Fresno
    7: [34.1, -117.3], // Inland Empire - centered on Riverside/San Bernardino
    8: [34.0522, -118.2437], // Los Angeles County - Los Angeles
    9: [33.7175, -117.8311], // Orange County - Orange County center
    10: [33.0, -117.0], // San Diego-Imperial - centered on San Diego
  };
  
  return regionCenters[regionId] || [36.7783, -119.4179]; // Default to California center
};

/**
 * Get map zoom level for a region
 * @param regionId Region ID (0 = All California, 1-10 = specific regions)
 * @returns Zoom level (higher = more zoomed in)
 */
export const getRegionZoom = (regionId: number): number => {
  // Zoom levels: higher number = more zoomed in
  const regionZooms: Record<number, number> = {
    0: 6, // All California - zoomed out to show entire state
    1: 7, // Superior California - medium zoom to show region
    2: 7, // North Coast - medium zoom
    3: 9, // San Francisco Bay Area - zoomed in for detail
    4: 8, // Northern San Joaquin Valley - medium-high zoom
    5: 7, // Central Coast - medium zoom
    6: 7, // San Joaquin Valley - medium zoom
    7: 8, // Inland Empire - medium-high zoom
    8: 9, // Los Angeles County - zoomed in for detail
    9: 9, // Orange County - zoomed in for detail
    10: 8, // San Diego-Imperial - medium-high zoom
  };
  
  return regionZooms[regionId] || 6; // Default to California-wide zoom
};
